import { ColorEnum } from '@/constants/color'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import { useCustomerLookupModalState } from '@/features/pos/components/CustomerProfile/hooks/useCustomerLookupModalState'
import BottomActions from '@/features/pos/components/CustomerProfile/parts/BottomActions'
import FormContent from '@/features/pos/components/CustomerProfile/parts/FormContent'
import RightPanel from '@/features/pos/components/CustomerProfile/parts/RightPanel'
import { CustomerLookupSaveInput } from '@/features/pos/components/CustomerProfile/types'
import { CustomerInput } from '@/features/pos/components/OrderFinalization/form/types'
import Modal from '@/features/ui/components/Modal'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useMemo } from 'react'
import { Platform, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

interface ContentProps {
    deliveryTypeCategories: DeliveryTypeCategory[]
    draftDeliveryTypeCategory: DeliveryTypeCategory
    initialCustomer?: CustomerInput
    onCancel: () => void
    onDraftDeliveryTypeCategoryChange: (value: DeliveryTypeCategory) => void
    onResetDeliveryTypeCategory: () => void
    onSave: (input: CustomerLookupSaveInput) => void
}

const MODAL_VERTICAL_PADDING = 16
const EXTENDED_CONTENT_HEIGHT = 640
const BOTTOM_ACTIONS_HEIGHT = 72
const MIN_EXTENDED_CONTENT_HEIGHT = 480

const Content: FC<ContentProps> = ({
    deliveryTypeCategories,
    draftDeliveryTypeCategory,
    initialCustomer,
    onCancel,
    onDraftDeliveryTypeCategoryChange,
    onResetDeliveryTypeCategory,
    onSave,
}) => {
    const layout = useScreenLayout()
    const isCompactLayout = layout === 'compact'
    const { height: windowHeight } = useWindowDimensions()
    const insets = useSafeAreaInsets()
    const availableModalHeight =
        windowHeight - insets.top - insets.bottom - MODAL_VERTICAL_PADDING * 2
    const extendedContentHeight = Math.max(
        MIN_EXTENDED_CONTENT_HEIGHT,
        Math.min(EXTENDED_CONTENT_HEIGHT, availableModalHeight - BOTTOM_ACTIONS_HEIGHT)
    )

    const {
        addressInputRef,
        addressQuery,
        addressSections,
        customerResults,
        customerSearchDialCode,
        customerSearchPhone,
        handleAddressQueryChange,
        handleAddressSelect,
        handleCustomerSelect,
        handleReset,
        handleSave,
        name,
        nameInputRef,
        phone,
        phoneInputRef,
        requiredAddress,
        rightPanelState,
        searchNewCustomerDisabled,
        setName,
        setPhone,
    } = useCustomerLookupModalState({
        draftDeliveryTypeCategory,
        initialCustomer,
        onResetDeliveryTypeCategory,
        onSave,
    })

    const formContentProps = useMemo(
        () => ({
            addressInputRef,
            addressQuery,
            deliveryTypeCategories,
            draftDeliveryTypeCategory,
            name,
            nameInputRef,
            onAddressQueryChange: handleAddressQueryChange,
            onDeliveryTypeCategoryChange: onDraftDeliveryTypeCategoryChange,
            onNameChange: setName,
            onPhoneChange: setPhone,
            phone,
            phoneInputRef,
            requiredAddress,
        }),
        [
            addressInputRef,
            addressQuery,
            deliveryTypeCategories,
            draftDeliveryTypeCategory,
            handleAddressQueryChange,
            name,
            nameInputRef,
            onDraftDeliveryTypeCategoryChange,
            phone,
            phoneInputRef,
            requiredAddress,
            setName,
            setPhone,
        ]
    )

    return (
        <Modal
            avoidKeyboard={isCompactLayout && Platform.OS === 'ios'}
            collapsable={false}
            contentStyle={[
                styles.modalContent,
                isCompactLayout && styles.modalContentCompact,
                { maxHeight: availableModalHeight },
            ]}
            contentVariant="wide"
            keyboardAvoidanceVariant="padding"
            respectTopSafeArea
        >
            <View style={[styles.modalBody, isCompactLayout && styles.modalBodyCompact]}>
                {isCompactLayout ? (
                    <View style={styles.compactContent}>
                        <ScrollView
                            contentContainerStyle={styles.compactScrollContent}
                            keyboardShouldPersistTaps="always"
                            showsVerticalScrollIndicator={false}
                            style={styles.compactScroll}
                        >
                            <View style={styles.compactLeftSection}>
                                <FormContent compact {...formContentProps} />
                            </View>

                            <View style={styles.compactDivider} />

                            <View style={styles.compactRightSection}>
                                <RightPanel
                                    addressSections={addressSections}
                                    compact
                                    customerResults={customerResults}
                                    onAddressSelect={handleAddressSelect}
                                    onCustomerSelect={handleCustomerSelect}
                                    searchedPhone={customerSearchPhone}
                                    searchedPhoneDialCode={customerSearchDialCode}
                                    state={rightPanelState}
                                />
                            </View>
                        </ScrollView>
                    </View>
                ) : (
                    <View
                        style={[
                            styles.content,
                            styles.contentExtended,
                            { height: extendedContentHeight },
                        ]}
                    >
                        <View style={styles.leftPanel}>
                            <FormContent {...formContentProps} />
                        </View>
                        <View style={styles.divider} />
                        <View style={styles.rightPanel}>
                            <RightPanel
                                addressSections={addressSections}
                                customerResults={customerResults}
                                onAddressSelect={handleAddressSelect}
                                onCustomerSelect={handleCustomerSelect}
                                searchedPhone={customerSearchPhone}
                                searchedPhoneDialCode={customerSearchDialCode}
                                state={rightPanelState}
                            />
                        </View>
                    </View>
                )}
                <BottomActions
                    compact={isCompactLayout}
                    onCancel={onCancel}
                    onConfirm={handleSave}
                    onSearchNewCustomer={handleReset}
                    searchNewCustomerDisabled={searchNewCustomerDisabled}
                />
            </View>
        </Modal>
    )
}

const styles = StyleSheet.create({
    modalContent: {
        backgroundColor: ColorEnum.GRAY_100,
        maxWidth: 1140,
        width: '88%',
    },
    modalContentCompact: {
        maxWidth: '93%',
        width: '93%',
    },
    modalBody: {
        backgroundColor: ColorEnum.GRAY_100,
        borderRadius: 16,
        maxHeight: '100%',
        overflow: 'hidden',
    },
    modalBodyCompact: {
        maxHeight: '100%',
    },
    content: {
        minHeight: 0,
    },
    contentExtended: {
        flexDirection: 'row',
        flexShrink: 1,
        minHeight: 0,
    },
    leftPanel: {
        minHeight: 0,
        padding: 32,
        width: '50%',
    },
    rightPanel: {
        flex: 1,
        minHeight: 0,
        padding: 32,
    },
    divider: {
        backgroundColor: ColorEnum.GRAY_200,
        width: 1,
    },
    compactContent: {
        flexShrink: 1,
    },
    compactScroll: {
        flexShrink: 1,
        maxHeight: '100%',
    },
    compactScrollContent: {
        flexGrow: 1,
        paddingBottom: 32,
    },
    compactLeftSection: {
        gap: 24,
        paddingBottom: 24,
        paddingHorizontal: 24,
        paddingTop: 24,
        width: '100%',
    },
    compactDivider: {
        backgroundColor: ColorEnum.GRAY_200,
        height: 1,
        width: '100%',
    },
    compactRightSection: {
        minHeight: 0,
        paddingHorizontal: 24,
        paddingTop: 24,
        width: '100%',
    },
})

export default Content
